import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useStore } from 'react-redux';
import { TableIdContext } from 'contexts/TableIdContext';
import { TABLE_TRANSITION_MS, ViewManagerContext } from 'contexts/ViewManagerContext';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useExitToNextTable } from 'hooks/useExitToNextTable';
import {
  cancelSitGoRegistration,
  clearSitGoResult,
  sitGoRematchRegistered
} from 'reducers/sitGoResult';
import useGameService from 'services/GameService';
import type { RootState } from 'store';
import { isStillOffLobby, retryPastTransition } from 'utils/navigationRetry';
import { markPlayerExitedTable } from 'utils/recentlyExitedTables';

import TournamentDetailModal, {
  SitGoRematchOptions
} from 'components/TournamentDetailModal/TournamentDetailModal';

import { landOnSitGoTab } from './sitGoLobbyLanding';
import { findRematchTournament } from './sitGoResultDetection';
import { useSitGoResultEntry } from './useSitGoResult';

/**
 * EN-11109: Yes on the Sit & Go result offer. Opens the lobby's own Confirm
 * Registration (TournamentDetailModal, through autoRegisterOnOpen) for the next
 * table of the same template, hosted on the table because the lobby — the
 * modal's usual host — is unmounted while a table is on screen.
 *
 *  - Confirm registers as today, balance check included; Registration Failed
 *    offers Cashier and Cancel as today.
 *  - Cancel (either screen) returns to the result modal, the offer still open.
 *  - Success opens the new table in place of the finished one, which is then
 *    left and closed (it no longer counts toward the table cap).
 *  - No 'R' table of the template listed: best effort, the lobby's Sit & Go
 *    tab on the format played. The finished table stays open behind it.
 *  - The table fills up first: the registration moves on to the template's
 *    next table without another Confirm (TournamentDetailModal,
 *    REMATCH_MAX_MOVES); if none takes it, "Go to lobby" on the No open table
 *    notice lands there too.
 *
 * Mounted beside SitGoResultModal; renders nothing until Yes.
 */
const SitGoRematchRegistration = () => {
  const entry = useSitGoResultEntry();
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const gameID = String(useContext(TableIdContext) || '');
  const { returnToLobby } = useContext(ViewManagerContext);
  const { leaveTable } = useGameService();
  const exitToNextTable = useExitToNextTable();
  const sitAndGoList = useAppSelector((state) => (state as any).lobby?.tournament?.sitAndGoList);
  const registrationOpen = !!entry?.registrationOpen;
  const [target, setTarget] = useState<any | null>(null);
  const [autoRegister, setAutoRegister] = useState(false);

  // The lobby's Sit & Go tab on the format played, the finished table kept
  // open behind it: no next table to register at.
  const goToSitGoLobby = useCallback(() => {
    const capacity = store.getState().sitGoResult?.byGameId?.[gameID]?.outcome?.capacity ?? 0;
    dispatch(cancelSitGoRegistration({ gameID, reopen: false }));
    landOnSitGoTab(capacity);
    // returnToLobby drops a call inside a view switch's debounce without a
    // word, so it is retried while the view has not left this table, as Go to
    // lobby does.
    retryPastTransition(() => isStillOffLobby(gameID), returnToLobby);
  }, [dispatch, gameID, returnToLobby, store]);

  // Pin the next table when Yes is pressed: a list push mid-confirm must not
  // swap the tournament the player is confirming.
  useEffect(() => {
    if (!registrationOpen || !entry) {
      setTarget(null);
      setAutoRegister(false);
      return;
    }
    const match = findRematchTournament(entry.template, sitAndGoList);
    if (match) {
      setTarget(match);
      setAutoRegister(true);
      return;
    }
    goToSitGoLobby();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrationOpen]);

  const handleCancel = useCallback(
    () => dispatch(cancelSitGoRegistration({ gameID })),
    [dispatch, gameID]
  );
  // The detail view's X, or Cashier: the offer waits on the felt.
  const handleClose = useCallback(
    () => dispatch(cancelSitGoRegistration({ gameID, reopen: false })),
    [dispatch, gameID]
  );
  const handleRegistered = useCallback(() => {
    // Once per offer: the grid stays mounted behind the single-table view, so
    // this table's grid cell and its panel both host a Confirm and both hear
    // the reply. Read from the store, not a render, so the second call in the
    // same tick sees the first.
    const current = store.getState().sitGoResult?.byGameId?.[gameID];
    if (!current?.registrationOpen) return;
    dispatch(sitGoRematchRegistered(gameID));
    // After the view has moved to the new table: tearing the finished one
    // down mid cross-fade would blank its frame.
    window.setTimeout(() => {
      if (leaveTable(true, true, true)) {
        markPlayerExitedTable(gameID, { outOfTournament: true });
      }
      dispatch(clearSitGoResult(gameID));
      exitToNextTable(gameID, { navigate: false });
    }, TABLE_TRANSITION_MS + 50);
  }, [dispatch, exitToNextTable, gameID, leaveTable, store]);

  // The table filled up before the registration reached it: the next open
  // table of the same template, from the latest lobby list (read from the
  // store: the push that lists it can land between renders), skipping the
  // tables that filled up. The pin above picks the same way.
  const handleFindNextTable = useCallback(
    (excludeTournamentIds: string[]) => {
      const state = store.getState() as any;
      const template = state.sitGoResult?.byGameId?.[gameID]?.template ?? null;
      const list = state.lobby?.tournament?.sitAndGoList;
      if (!Array.isArray(list)) return null;
      const skip = new Set(excludeTournamentIds.map(String));
      return findRematchTournament(
        template,
        list.filter((row: any) => !skip.has(String(row?.tournamentId ?? row?.id)))
      );
    },
    [gameID, store]
  );

  const rematch = useMemo<SitGoRematchOptions>(
    () => ({
      leavingGameID: gameID,
      onCancel: handleCancel,
      onRegistered: handleRegistered,
      findNextTable: handleFindNextTable,
      onNoOpenTable: goToSitGoLobby
    }),
    [gameID, handleCancel, handleRegistered, handleFindNextTable, goToSitGoLobby]
  );

  if (!entry || !registrationOpen || !target) return null;

  return (
    <TournamentDetailModal
      open
      onClose={handleClose}
      tournament={target}
      autoRegisterOnOpen={autoRegister}
      onAutoRegisterConsumed={() => setAutoRegister(false)}
      rematch={rematch}
    />
  );
};

export default SitGoRematchRegistration;
